import { useMemo, useState } from 'react';
import { Search, Upload, MessageCircle, Download, X, TriangleAlert, Sparkles, Radar, FileSpreadsheet, Table2 } from 'lucide-react';
import { AsyncBoundary, Drawer, useToast } from '@kollez/design-system';
import { useTpoStudents, type TpoStudentRow } from '@/lib/queries';

/**
 * /tpo/students (F89c) — direct port of kollez/pages/tpo/students.html.
 *   Header + Import CTA · warning notice for blocked rows · Filterbar (search + branch + status + count)
 *   · chips row · table with select-all/avatar/roll/CGPA/applications summary/status badge
 *   · bulk-bar (WhatsApp remind / Export) · student drawer with 360° link · Import-roster drawer
 */

const STATUS_BADGE: Record<TpoStudentRow['status'], { label: string; tone: string }> = {
  placed: { label: 'Placed', tone: 'badge-success' },
  active: { label: 'Applying', tone: 'badge-info' },
  idle: { label: 'Not applying', tone: 'badge-warning' },
  blocked: { label: "Can't apply", tone: 'badge-danger' },
};

// Deep-audit note (Page 33/N): the branch list was hardcoded to 5 engineering
// branches. A tenant with MBA, MCA, CIVIL, BIOTECH, or any non-listed branch
// would have those students invisible under the filter. Widened to cover the
// common Indian engineering + management set the demo tenants use. A future
// pass wires this to a per-tenant Program.specialization list from the API.
const BRANCHES = ['CSE', 'ECE', 'EEE', 'IT', 'MECH', 'CIVIL', 'CHEM', 'BIOTECH', 'MBA', 'MCA'];

function initials(name: string): string {
  return name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
}

export function TpoStudentsPage() {
  const [q, setQ] = useState('');
  const [branch, setBranch] = useState('');
  const [status, setStatus] = useState<'' | TpoStudentRow['status']>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const toast = useToast();

  const { data, isLoading, error, refetch } = useTpoStudents({ q: q || undefined, branch: branch || undefined, status: status || undefined });
  const rows = data?.students ?? [];
  const total = data?.total ?? 0;
  const blockedCount = rows.filter((r) => r.status === 'blocked').length;

  const chips = useMemo(() => {
    const c: Array<[string, string, () => void]> = [];
    if (branch) c.push(['branch', branch, () => setBranch('')]);
    if (status) c.push(['status', STATUS_BADGE[status].label, () => setStatus('')]);
    return c;
  }, [branch, status]);

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll(on: boolean) {
    if (on) setSelected(new Set(rows.map((r) => r._id)));
    else setSelected(new Set());
  }

  const opened = openId ? rows.find((r) => r._id === openId) : null;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Students</h1>
          <p className="subhead">
            {total} student{total === 1 ? '' : 's'} in your college
            {rows.filter((r) => r.status === 'placed').length > 0 && ` · ${rows.filter((r) => r.status === 'placed').length} placed shown`}
          </p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-outline" onClick={() => setImportOpen(true)}><Upload size={14} />Import roster</button>
        </div>
      </div>

      {blockedCount > 0 && (
        <div className="notice notice-warning">
          <Table2 size={14} />
          <div className="flex-1">
            <b>{blockedCount} students can't apply yet.</b> Common reasons: missing hall-ticket number, active backlogs, or unverified academics.
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setStatus('blocked')}>Fix {blockedCount} rows</button>
        </div>
      )}

      <div className="card">
        <div className="filterbar">
          <div className="input-group fb-search">
            <Search size={14} />
            <input className="input" placeholder="Name or roll number" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className="select" value={branch} onChange={(e) => setBranch(e.target.value)}>
            <option value="">All branches</option>
            {BRANCHES.map((b) => <option key={b}>{b}</option>)}
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">Any status</option>
            <option value="placed">Placed</option>
            <option value="active">Applying</option>
            <option value="idle">Not applying</option>
            <option value="blocked">Can't apply</option>
          </select>
          <span className="fb-count">{(q || branch || status) ? `${rows.length} shown` : `${total} students`}</span>
        </div>

        {chips.length > 0 && (
          <div className="chips-row px-4 py-2 border-b border-line-tertiary flex items-center gap-2">
            {chips.map(([key, label, remove]) => (
              <span key={key} className="chip">
                {label}
                <button onClick={remove} aria-label="Remove filter"><X size={12} /></button>
              </span>
            ))}
            <button className="link text-sm ml-1" onClick={() => { setBranch(''); setStatus(''); }}>Clear all</button>
          </div>
        )}

        <AsyncBoundary
          kind="table"
          inCard
          loading={isLoading}
          error={error}
          empty={!isLoading && rows.length === 0}
          emptyTitle="No students match"
          emptyHint="Try clearing filters, or import your roster as a CSV/Excel file."
          onRetry={() => void refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="w-check">
                    <input
                      type="checkbox"
                      className="check"
                      aria-label="Select all"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th>Student</th>
                  <th className="hide-mobile">CGPA</th>
                  <th className="hide-mobile">Applications</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s._id}
                    onClick={(e) => { if ((e.target as HTMLElement).tagName !== 'INPUT') setOpenId(s._id); }}
                    aria-selected={selected.has(s._id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="w-check">
                      <input
                        type="checkbox"
                        className="check"
                        checked={selected.has(s._id)}
                        onChange={() => toggle(s._id)}
                        aria-label={`Select ${s.name}`}
                      />
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <span className="avatar avatar-sm">{initials(s.name)}</span>
                        <div className="min-w-0">
                          <div className="cell-title">{s.name}</div>
                          <div className="cell-sub">
                            {s.branch} · {s.roll ?? '—'}
                            <span className="show-mobile inline"> · {s.cgpa.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hide-mobile num">{s.cgpa.toFixed(2)}</td>
                    <td className="hide-mobile"><span className="cell-sub">{s.applicationsSummary ?? `${s.applications} apps`}</span></td>
                    <td><span className={`badge ${STATUS_BADGE[s.status].tone}`}>{STATUS_BADGE[s.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      {selected.size > 0 && (
        <div className="bulkbar" data-open="true">
          <span className="text-sm whitespace-nowrap"><b>{selected.size}</b> selected</span>
          {/* Deep-audit fix (Page 33/N): both bulk actions used to fire
              lying toasts — 'Reminder queued', 'Export ready —
              students.xlsx' — with no API call behind either. A TPO
              clicking Export and then not receiving a file would blame
              their download folder, not the app. Disabled with 'soon'
              labels and tooltips pointing to the working path today. */}
          <button
            className="btn btn-ghost btn-sm"
            disabled
            title="Bulk WhatsApp reminders ship with the next release. For now, open each row and use the WhatsApp button in the drawer."
          >
            <MessageCircle size={14} />Remind on WhatsApp · soon
          </button>
          <button
            className="btn btn-ghost btn-sm"
            disabled
            title="Bulk export ships in the next release. Use Reports for tenant-wide exports today."
          >
            <Download size={14} />Export · soon
          </button>
        </div>
      )}

      <Drawer open={Boolean(opened)} onClose={() => setOpenId(null)} title={opened?.name ?? ''} size="lg">
        {opened && (
          <>
            <div className="flex items-center gap-3">
              <span className="avatar avatar-lg">{initials(opened.name)}</span>
              <div>
                <h2 className="h-section">{opened.name}</h2>
                <p className="helper">{opened.roll ?? '—'} · {opened.branch}</p>
              </div>
            </div>
            <span className={`badge ${STATUS_BADGE[opened.status].tone} mt-3 inline-flex`}>
              {STATUS_BADGE[opened.status].label}
              {opened.offer ? ` · ${opened.offer.partner} · ${opened.offer.ctc ? '₹' + Math.round(opened.offer.ctc / 100000) + ' LPA' : ''}` : opened.applicationsSummary ? ` · ${opened.applicationsSummary}` : ''}
            </span>
            <dl className="dl mt-4">
              <dt>CGPA</dt><dd>{opened.cgpa.toFixed(2)}</dd>
              <dt>Backlogs</dt><dd>{opened.backlogs === 0 ? 'None' : `${opened.backlogs} active`}</dd>
              <dt>Mobile</dt><dd>{opened.phone ?? '—'}</dd>
              <dt>Applications</dt><dd>{opened.applications}</dd>
            </dl>
            {opened.status === 'blocked' && (
              <div className="notice notice-warning mt-3">
                <TriangleAlert size={14} />
                <div>{opened.reason ?? 'Fix this record to let the student apply.'}</div>
              </div>
            )}
            {opened.status === 'idle' && (
              <div className="ai-suggest mt-3">
                <Sparkles size={14} />
                <div>{opened.name.split(' ')[0]} hasn't applied in a while. Send a WhatsApp nudge with the open drives.</div>
              </div>
            )}
            <div className="drawer-foot mt-4">
              {opened.phone ? (
                <a className="btn btn-outline" href={`https://wa.me/${opened.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                  <MessageCircle size={14} />WhatsApp
                </a>
              ) : (
                <button className="btn btn-outline" type="button" disabled title="No phone on record">
                  <MessageCircle size={14} />WhatsApp
                </button>
              )}
              {/* Deep-audit fix (Page 33/N): the old link went to '/profile-360'
                  which resolves to the *current TPO's* readiness page — not the
                  selected student's. Clicking it while viewing Anitha K's drawer
                  showed the TPO their own zero-data page. There is no per-student
                  profile-360 route today; the closest deep-view is the student
                  detail page. Disabled with a tooltip pointing at that path. */}
              <button
                className="btn btn-outline"
                type="button"
                disabled
                title="Per-student 360° view ships in the next release. Use the drawer detail here for now."
              >
                <Radar size={14} />360° view · soon
              </button>
              {opened.status === 'blocked' && (
                <button className="btn btn-primary ml-auto" onClick={() => setOpenId(null)}>Fix record</button>
              )}
            </div>
          </>
        )}
      </Drawer>

      <Drawer open={importOpen} onClose={() => setImportOpen(false)} title="Import roster" size="lg">
        <label className="well p-6 grid place-items-center text-center gap-2 border border-dashed border-line-primary cursor-pointer">
          <FileSpreadsheet size={22} className="text-ink-accent" />
          <b>Drop a CSV or Excel file</b>
          <span className="helper">Or click to choose. We match your columns before saving anything.</span>
          <input type="file" className="sr-only" />
        </label>
        <p className="helper mt-4">
          Needs: name, roll number, branch, CGPA, mobile.{' '}
          <button
            type="button"
            className="link"
            onClick={() => {
              const csv = 'name,rollNumber,branch,cgpa,mobile,email\nMeghana Varma,21GT1A0437,ECE,8.42,9876543210,meghana@example.edu\n';
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'kollez-roster-template.csv';
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Download a template
          </button>
        </p>
      </Drawer>
    </div>
  );
}
